'use client';

import { useEffect, useState } from 'react';

import { useShareDialog } from '@ps/features/sharing/useShareDialog';
import type { BioContext } from '@ps/functions/bio/bioContext';
import { bioUrl } from '@ps/functions/bio/bioUrls';
import { PopImage } from '@ps/ui/atoms/pop-image';
import { cn } from '@ps/ui/utils';
import { Check, Copy, Download, Globe, Share } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

import { isBioUrlSnapshot } from '../functions/bioUrlSnapshot';
import type { ContactCardViewModel } from '../functions/contactCardViewModel';
import {
  downloadContactCardVcf,
  generateVcfContent,
} from '../functions/vcfUtils';

/** What the contact-card QR encodes. */
export type ContactCardQrEncoding = 'vcard' | 'url';

interface Props {
  vm: ContactCardViewModel;
  /** The page's bio context — makes the website/share/QR links custom-domain aware. */
  bioContext: BioContext;
  /** Merged onto the root so each host controls layout/scroll. */
  className?: string;
  /**
   * What the QR encodes. `'url'` (default) encodes the short
   * `<bio-home>/contact-card/download` link — scanning opens the .vcf through
   * the OS's full Contacts parser, which reads the structured `N` and splits
   * first/last correctly (the camera quick-add parser mis-maps an embedded
   * vCard, dumping the whole name into First Name). `'vcard'` embeds the full
   * vCard for offline scan-to-import, at the cost of that name mis-split.
   */
  qrEncoding?: ContactCardQrEncoding;
}

/**
 * Pure contact-card body — the shared content orchestrated by every host (the
 * intercepting-modal route, the standalone page, and the legacy dialog): share
 * action, avatar / name / bio / website links (with copy), the vCard QR, and the
 * download CTA. Layout-agnostic — the host provides the shell + the close/back
 * control. The content is vertically centered (mobile parity) and scrolls if it
 * overflows; share + download + copy are self-contained client actions.
 *
 * 1:1 with the legacy `ContactCardContent`.
 */
export function ContactCardView({
  vm,
  bioContext,
  className,
  qrEncoding = 'url',
}: Props) {
  const [isDownloading, setIsDownloading] = useState(false);
  const { openShare } = useShareDialog();

  // The bio home is built from the context's `domain` (the creator's custom
  // domain when set, else the default host) — deterministic + SSR-safe, so no
  // `window.location.origin` / post-hydration correction is needed.
  const bioHome = bioUrl(bioContext, { share: true }).home();

  // The card's primary link is the creator's bio home. Setup pre-fills
  // `websiteUrl1` with a DEFAULT-domain snapshot of it (`${config.domain}/<code>`)
  // — treat that snapshot as "the bio link" and render the LIVE, custom-domain
  // aware `bioHome` instead of the stale value. A genuine external website (one
  // that isn't that snapshot) is shown as-is; an empty field falls back to the
  // bio home too.
  const website1 = isBioUrlSnapshot(vm.websiteUrl1, bioContext.urlCode)
    ? bioHome
    : (vm.websiteUrl1 ?? bioHome);
  const vcfData = { ...vm.vcfData, url: website1 };

  // Just the bio PATH (no origin), e.g. `/shivams19` or `` for an independent
  // domain — the QR/download link is rebuilt against the LIVE origin below.
  const bioPath = bioHome.replace(/^https?:\/\/[^/]+/, '').replace(/\/+$/, '');

  // Seed the `'url'` QR SYNCHRONOUSLY (server + first client render) so it's
  // present even where the post-mount effect doesn't run (static prerender /
  // non-hydrating island). `bioHome` carries the build-time origin: on prod /
  // custom domain it's absolute → scannable as-is; on preview it's root-relative
  // and the effect upgrades it. The `'vcard'` seed stays null — its REV
  // timestamp would hydration-mismatch.
  const [qrContent, setQrContent] = useState<string | null>(() =>
    qrEncoding === 'url'
      ? `${bioHome.replace(/\/+$/, '')}/contact-card/download`
      : null,
  );
  useEffect(() => {
    // The visitor is ON the bio host, so the QR MUST point at the CURRENT origin
    // to be reachable when scanned — never the build-time origin, which is
    // `localhost` on a LAN-IP session and `/` on a preview channel. Build it
    // from `window.location.origin` + the bio path in every case.
    const origin = window.location.origin;
    if (qrEncoding === 'url') {
      setQrContent(`${origin}${bioPath}/contact-card/download`);
      return;
    }
    // 'vcard': `website1` may be a genuine EXTERNAL site — keep an absolute url
    // as-is; only resolve a relative bio-home fallback against the live origin.
    const websiteUrl = website1.startsWith('http')
      ? website1
      : `${origin}${website1.startsWith('/') ? '' : '/'}${website1}`;
    setQrContent(generateVcfContent({ ...vm.vcfData, url: websiteUrl }));
  }, [bioPath, qrEncoding, website1, vm.vcfData]);

  const onDownload = async () => {
    if (isDownloading) {
      return;
    }
    setIsDownloading(true);
    try {
      await downloadContactCardVcf({
        contactCardData: vcfData,
        avatarUrl: vm.avatarUrl,
      });
    } finally {
      setIsDownloading(false);
    }
  };

  // Open the app-wide share modal (copy-link + social targets) — works on
  // desktop too, unlike bare `navigator.share`. The provider is mounted on both
  // the visitor bio layout and the dashboard layout, so it's available on every
  // surface this card renders on. Share the contact-card page itself
  // (`/<urlCode>/contact`), not the bio home — `{ share: true }` yields the
  // clean absolute permalink.
  const onShare = () => {
    openShare({
      url: bioUrl(bioContext, { share: true }).contact(),
      title: vm.displayName,
    });
  };

  return (
    <div className={cn('flex min-h-0 flex-col text-white', className)}>
      <div className="flex shrink-0 justify-end">
        <button
          type="button"
          onClick={onShare}
          aria-label="Share"
          className="hover:text-mint-green-300 text-white transition"
        >
          <Share className="h-6 w-6" aria-hidden />
        </button>
      </div>

      {/* Centered content — `min-h-full` + `justify-center` centers when it fits,
          and top-aligns + scrolls when it overflows (no clipped avatar). */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="flex min-h-full flex-col items-center justify-center gap-2 py-4">
          {vm.avatarUrl ? (
            <PopImage
              src={vm.avatarUrl}
              alt=""
              width={112}
              height={112}
              resizing={{ width: 224, height: 224, fit: 'cover' }}
              resizeMode="cover"
              className="mb-2 h-20 w-20 rounded-full ring-4 ring-white/10 lg:h-28 lg:w-28"
            />
          ) : null}

          <h2 className="text-center text-[20px] font-semibold leading-7 text-white">
            {vm.displayName}
          </h2>

          {vm.bioText ? (
            <p className="text-center text-[15px] font-light text-white/90">
              {vm.bioText}
            </p>
          ) : null}

          {website1 ? <CopyableUrl url={website1} /> : null}
          {vm.websiteUrl2 ? <CopyableUrl url={vm.websiteUrl2} /> : null}

          <div className="relative mt-6 w-[min(280px,78vw)] lg:w-[240px]">
            <span className="absolute -top-2 right-[-10px] z-10 rounded bg-black px-2 py-1 text-[11px] font-medium text-white">
              Scan Me
            </span>
            <div className="rounded-2xl border border-white/15 bg-white p-3 [&>svg]:h-auto [&>svg]:w-full">
              {qrContent ? (
                <QRCodeSVG
                  value={qrContent}
                  size={256}
                  level="L"
                  marginSize={2}
                  bgColor="#FFFFFF"
                  fgColor="#000000"
                />
              ) : (
                // `aspect-*` utilities produce no CSS in this repo — reserve the
                // square via inline `aspectRatio` so the box doesn't collapse.
                <div
                  className="w-full"
                  style={{ aspectRatio: '1 / 1' }}
                  aria-hidden
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="shrink-0 pb-4 pt-3">
        <button
          type="button"
          onClick={onDownload}
          disabled={isDownloading}
          className={cn(
            'flex w-full items-center justify-center gap-2 rounded-full py-3.5 text-[15px] font-semibold text-black transition',
            'bg-[#FF50A0] hover:bg-[#ff66ac] disabled:opacity-60',
          )}
        >
          <Download className="h-5 w-5" aria-hidden />
          {isDownloading ? 'Downloading…' : 'Download my contact details'}
        </button>
      </div>
    </div>
  );
}

/** Full URL + a copy-to-clipboard icon (green → check on copy). Mirrors legacy
 *  `DisplayTextWithCopyIcon`. */
function CopyableUrl({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);

  const onCopy = () => {
    if (typeof navigator === 'undefined' || !navigator.clipboard) {
      return;
    }
    void navigator.clipboard
      .writeText(url)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {});
  };

  return (
    <div className="flex max-w-full items-center justify-center gap-1.5">
      <Globe className="text-mint-green-300 h-4 w-4 shrink-0" aria-hidden />
      <span className="truncate text-[16px] font-medium text-white">{url}</span>
      <button
        type="button"
        onClick={onCopy}
        aria-label="Copy link"
        className="text-mint-green-300 shrink-0 transition hover:opacity-80"
      >
        {copied ? (
          <Check className="h-[18px] w-[18px]" aria-hidden />
        ) : (
          <Copy className="h-[18px] w-[18px]" aria-hidden />
        )}
      </button>
    </div>
  );
}
